import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'

import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import { IconButton } from '@material-ui/core'
import AddSustIcon from '@material-ui/icons/Eco'
import { GetProjectElementNodesQuery } from 'api/graphQL/queries/projectElements.queries'
import clsx from 'clsx'
import { GetProjectElementNodesInput } from 'models/getProjectElementNodesInput'
import { GetProjectElementNodesResponse } from 'models/projectElementNode'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import { NormalizedDocumentTemplateViewItem } from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import React from 'react'
import AddSustainabilityTopicDialog from './AddSustainabilityTopicDialog'

const useAddSustainabilityStyles = makeStyles((theme: Theme) => ({
    AddSustainabilityTopic: {
        display: 'flex',
        height: theme.spacing(5),
        width: theme.spacing(4.4),
        position: 'relative',
        backgroundColor: 'rgb(39,146,74)',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(0.5),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    addPTCircle: {
        backgroundColor: '#F78909',
        height: 'inherit',
        padding: theme.spacing(0.38, 0.63),
        '& .MuiIconButton-root': {
            padding: 0,
        },
    },
    avatar: {
        backgroundColor: theme.palette.common.white,
        color: '#F78909',
        height: theme.spacing(3.15),
        width: theme.spacing(3.15),
        fontSize: pxToRem(20),
    },
    hide: {
        display: 'none',
    },
    addSustIcon: {
        color: theme.palette.common.white,
        fontSize: pxToRem(22),
        margin: theme.spacing(0, 0.5),
        borderRadius: '0px 2px 2px 0px',
        backgroundColor: 'rgb(39,146,74)',
    },
}))

interface AddSustainabilityProps {
    template?: NormalizedDocumentTemplateViewItem
    parent?: NormalizedDocumentTemplateViewItem
    resetInput?: () => void
}

const AddSustainabilityTopic: React.FC<AddSustainabilityProps> = ({
    template,
    parent,
    resetInput,
}) => {
    const classes = useAddSustainabilityStyles()
    const { handleAddSustainabilityTopic } = React.useContext(AreaContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        isInsertTopicDialogOpen,
        setIsInsertTopicDialogOpen,
    ] = React.useState<boolean>(false)

    const [getElements, { loading: topicListLoading }] = useLazyQuery<
        GetProjectElementNodesResponse | null,
        GetProjectElementNodesInput
    >(GetProjectElementNodesQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data: GetProjectElementNodesResponse | null) => {
            const elemType =
                data?.getProjectElementNodes?.projectElementNodes?.[0]
                    ?.elementType

            if (elemType === 'SUSTAINABILITY') {
                const topicList = (
                    data?.getProjectElementNodes?.projectElementNodes || []
                ).map((topic) => {
                    return {
                        value: topic
                            .text!.split('|')[0]
                            .concat(' - ')
                            .concat(topic.text!.split('|')[1]),
                        name: topic.baseElementId,
                    }
                })
                setTopics(topicList)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on topic autosuggest in dialog',
                    variant: 'error',
                    id: `AddSustainabilityTopicDialog_TopicAutosuggestError`,
                },
            })
        },
    })

    const onInsertTopicClicked = React.useCallback(() => {
        resetInput?.()
        setIsInsertTopicDialogOpen((past: boolean) => !past)
    }, [setIsInsertTopicDialogOpen, isInsertTopicDialogOpen, template])

    const [topics, setTopics] = React.useState<
        Record<nameValuePropType, string>[] | null
    >([])

    const fetchElements = React.useCallback(
        (moreInput) => {
            getElements({
                variables: {
                    input: {
                        projectId: 'DraftMasterSpecProject',
                        limit: 1000,
                        skip: 0,
                        onlyElementsAssignedToProject: false,
                        isFromBPElementLibView: false,
                        projectElementType: ['SUSTAINABILITY'],
                        parentNodeId: 'root',
                        isProductFamilyGroupView: false,
                        ...moreInput,
                    },
                },
            })
        },
        [getElements]
    )

    const handleSearch = React.useCallback(
        (value?: string | boolean | null) => {
            fetchElements(
                !!value
                    ? {
                          searchFilters: [
                              {
                                  projectElementType: 'SUSTAINABILITY',
                                  searchText: value,
                              },
                          ],
                      }
                    : {}
            )
        },
        [fetchElements]
    )

    const handleOKPrompt = React.useCallback(
        (val: Record<nameValuePropType, string>[]) => {
            const arrTopicIds = val.map((topic) => topic.name)
            handleAddSustainabilityTopic?.(template!, parent!, arrTopicIds)
            onInsertTopicClicked()
        },
        [onInsertTopicClicked, template, parent]
    )

    return (
        <div className={classes.AddSustainabilityTopic}>
            <IconButton
                aria-label="add-newTopic"
                data-testid="addNewTopicButton"
                onMouseDown={onInsertTopicClicked}
            >
                <AddSustIcon
                    data-testid="addSustButton"
                    className={clsx(classes.addSustIcon)}
                    fontSize="inherit"
                ></AddSustIcon>
            </IconButton>

            {isInsertTopicDialogOpen && (
                <AddSustainabilityTopicDialog
                    openDialog={isInsertTopicDialogOpen}
                    handleCancel={onInsertTopicClicked}
                    handleOK={handleOKPrompt}
                    dialogHeading="Insert Topic"
                    dropDownLabel="Select from list..."
                    handleSearchInput={handleSearch}
                    loadingOptions={topicListLoading}
                    options={topics!}
                    okText="Confirm"
                />
            )}
        </div>
    )
}

export default AddSustainabilityTopic
